/**
 * Dalle specifiche si assume che il contratto da visualizzare sia sempre composto dalle seguenti parti
 * 1. Logo                      -> sezione con il logo
 * 2. Promo                     -> sezione descrittiva della promo
 * 3. Condizioni contrattuali   -> sezione descrittiva delle condizioni
 * 4. Radio option              -> sezione di accettazione 
 * 
 * A meno di formattazione e modifiche grafiche al testo non rispettare queste 'convenzioni' 
 * potrebbe portare a problemi sulla SetPromoData e ad errori nella gestione delle varie sezioni
 * del contratto lato FE. 
 */
import { Box, IconButton, Stack } from '@mui/material';
import React from 'react';
import { AiOutlineReload } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { StyledPageComment } from '../../components/StyledPageComment';
import { routes } from '../../constants/appRoutes';
import { useErrorNotify } from '../../contexts/AlertContext';
import { useLoaderContext } from '../../contexts/LoaderContext';
import { BaseModel, useProfileContext } from '../../contexts/ProfileContext';
import { GetContract, SetPromoData } from '../../shared/api.helpers';
import { Common, ServiceModels, SetPromoDataType } from '../../shared/api.helpers.types';
import { errorMessages } from '../../shared/translations';
import { FE_SETTINGS, Guid, _ } from '../../shared/utils';
import { VdsButton } from '../../vds';

//#region Utilities
export const promoSectionKeys = {
    logo: 'promo-logo',
    main: 'promo-main',
    conditions: 'promo-conditions',
    options: 'promo-options',
};

export const getSection = (selector: string) => document.querySelector(selector);

/** 
 * Map utilizzata per il rimpiazzo dei campi dinamici passati nell'html del contratto.
 * Un elmento è composto da una chiave rappresentante il nome 'assunto statico' del campo e il percorso da ricercare nel modello 
 * profile in response alla GetPromoData. 
 * 
 * Questa soluzione serve per far fronte ad eventuali cambiamenti sia lato key sua per un eventuale modifica della response 
*/
export const dynamicFieldMapFromProfile = new Map([
    ["@@CompanyName@@", "company.name"],
    ["@@CompanyVatCode@@", "company.vatCode"],
    ["@@CompanyAddress@@", "company.address"],
    ["@@CompanyCity@@", "company.city"],
    ["@@CompanyPostalCode@@", "company.postalCode"],
    ["@@CompanyEmail@@", "company.email"],
    ["@@CompanyPec@@", "company.pec"],
    ["@@DiscountedAmount@@", "article.DiscountedAmount"],
    ["@@UserSurname@@", "surname"],
    ["@@UserName@@", "name"],
    ["@@UserEmail@@", "email"],
    ["@@UserFiscalCode@@", "fiscalCode"],
]);

/**
 * Metodo ricorsivo di utility per la ricerca di un percorso all'interno di un oggetto
 * @param obj       modello tipo unknown rappresentante un oggetto
 * @param deepkey   percorso del tipo {key}.{key1}.{key2}...
 * @returns         il valore finale del path o una stringa vuota
 */
const deepObjSearch = (obj: unknown, deepkey: string) => {
    const splittedkey = deepkey.split('.');
    if (splittedkey.length > 1 && obj[splittedkey[0]]) {
        return deepObjSearch(obj[splittedkey[0]], splittedkey.splice(1, splittedkey.length - 1).join('.'));
    }
    return String(obj[deepkey] ?? '');
}
/**
 * Metodo di utility per la ricerca e la sostituzione delle 
 * variabili dinamiche all'interno del contratto
 * @param profile       modello tipo GetPromoDataResponse
 */
export const replaceDynamicFields = (profile: BaseModel) => {
    dynamicFieldMapFromProfile.forEach((mapvalue, mapkey) => {
        const dynamicFields = document.querySelectorAll<HTMLElement>(`[data-type="${mapkey}"]`);    // nel editor dei contratti sul campo viene aggiunto un attributo data-type="{key}" per i campi dinamici
        dynamicFields.forEach((element) => {
            const _value = deepObjSearch(profile, mapvalue);
            // su richiesta il campo dinamico riferito al @@CompanyName@@ deve avere uno stile diverso dagli altri campi dinamici... lo so...

            element.style.fontStyle = 'Roboto-regular';
            element.style.fontWeight = 'bold';
            element.style.color = 'rgba(90, 104, 114, 1)';

            if (mapkey === "@@CompanyName@@") {
                element.style.fontWeight = 'unset';
            }
            // su richiesta il campo dinamico riferito al @@DiscountedAmount@@ essendo in formato numerico deve essere visualizzato con la virgola
            if (mapkey === "@@DiscountedAmount@@") {
                const value = `${(+_value || 0).valueOf().toLocaleString('it-IT')}`;
                element.innerText = value;
                element.dataset.type = value;
            } else {
                element.innerText = _value;
                element.dataset.type = _value;
            }
        });
    });
}
/**
 * Metodo di utility per il recupero delle varie sezioni del contratto
 * come da descrizione del modulo
 */
const takeSections = (contracts: Common.ContentWithLocContent[]) => {

    return {
        section_logo: {
            ...contracts[0],
        },
        section_promo: {
            ...contracts[1],
        },
        section_conditions: {
            ...contracts[2],
        },
        section_options: {
            ...contracts[3],
        },
    };
}
//#endregion

//#region Promo section handlers
interface HTMLMainPromoFormatterProps {
    contract: Common.ContentWithLocContent;
    profile: BaseModel;
};
export const HTMLMainPromoFormatter: React.FC<HTMLMainPromoFormatterProps> = ({ profile, contract }) => {

    React.useEffect(() => {
        const SectionAcceptance = getSection(`.${promoSectionKeys.main}`);
        const checkbox = SectionAcceptance.querySelector('input[type="checkbox"]');
        checkbox?.remove();
        replaceDynamicFields(profile);
    }, [profile]);

    return (
        <div
            className={promoSectionKeys.main}
            dangerouslySetInnerHTML={{
                __html: _.decodeHtml(contract.localizedContent.text)
            }}
        />
    );
};
interface HTMLPromoConditionsFormatterProps {
    contract: Common.ContentWithLocContent;
    profile: BaseModel;
};
export const HTMLPromoConditionsFormatter: React.FC<HTMLPromoConditionsFormatterProps> = ({ profile, contract }) => {

    React.useEffect(() => {
        const SectionAcceptance = getSection(`.${promoSectionKeys.conditions}`);
        const checkbox = SectionAcceptance.querySelector('input[type="checkbox"]');
        checkbox?.remove();
        replaceDynamicFields(profile);
    }, [profile]);

    return (
        <div
            className={promoSectionKeys.conditions}
            dangerouslySetInnerHTML={{
                __html: _.decodeHtml(contract.localizedContent.text)
            }}
        />
    );
};
interface HTMLPromoOptionsFormatterProps {
    contract: Common.ContentWithLocContent;
    profile: BaseModel;
    setConfirm: React.Dispatch<React.SetStateAction<boolean>>;
};
export const HTMLPromoOptionsFormatter: React.FC<HTMLPromoOptionsFormatterProps> = ({ setConfirm, profile, contract }) => {

    React.useEffect(() => {

        if (!contract) return;

        const SectionAcceptance = getSection(`.${promoSectionKeys.options}`);

        const handleRadioChange = (event) => {

            const radioInput = SectionAcceptance.querySelectorAll('input[type="radio"]');
            const yesRadioBtn = radioInput[0];
            const noRadioBtn = radioInput[1];

            const confirm = +event.target.value === 0;
            setConfirm(confirm);

            if (confirm) {
                yesRadioBtn.setAttribute('checked', 'checked');
                noRadioBtn.removeAttribute('checked');
            } else {
                noRadioBtn.setAttribute('checked', 'checked');
                yesRadioBtn.removeAttribute('checked');
            }
        };

        const radioInput = SectionAcceptance.querySelectorAll('input[type="radio"]');

        if (radioInput?.length === 2) {

            /**
             * Il contract deve prevedere almeno due radio options
             * una per l'accettazione e una per il rifiuto
             */
            radioInput.forEach((el) => {
                el.addEventListener('change', handleRadioChange);
            })

            return () => {
                radioInput.forEach((el) => {
                    el.removeEventListener('change', handleRadioChange);
                })
            };
        }
    }, [contract]);

    React.useEffect(() => {
        const SectionAcceptance = getSection(`.${promoSectionKeys.options}`);
        const checkbox = SectionAcceptance.querySelector('input[type="checkbox"]');
        checkbox?.remove();
        replaceDynamicFields(profile);
    }, [profile]);

    return (
        <div
            className={promoSectionKeys.options}
            dangerouslySetInnerHTML={{
                __html: _.decodeHtml(contract.localizedContent.text)
            }}
        />
    );
};
//#endregion

export const ContractPage: React.FC = () => {

    const navigate = useNavigate();
    const setLoader = useLoaderContext();
    const errorNotify = useErrorNotify();
    const [profile] = useProfileContext();

    const [references, setReferences] = React.useState<ServiceModels.GetContractResponse | null>(null);

    const [sectionsLoading, setSectionsLoading] = React.useState<boolean | null>(true);
    const [sections, setSections] = React.useState<ReturnType<typeof takeSections> | null>(null);
    const [accepted, setAccepted] = React.useState<null | boolean>(null);

    const handleGetContract = () => {

        setLoader(true);
        GetContract({
            companyOwnCode: profile?.company?.code,
            contextName: profile.campaign.SellingInfo.LcmsContractName
        })
            .then((res) => {
                setLoader(false);
                const code = res.data.status?.code;
                if (!["311", "300"].includes(code)) {
                    setSectionsLoading(false);
                    const contentWithLocContent = res.data
                        .contextsWithContents
                        .contextWithContents
                        .contentsWithLocContents
                        .contentWithLocContent;

                    setReferences(res.data);
                    setSections(takeSections(contentWithLocContent));
                } else {
                    errorNotify(`${errorMessages.GetContract[code] || errorMessages?.GeneralError} (Codice errore: ${code || 500})`);
                    setSectionsLoading(null);
                }
            })
            .catch((err) => {
                setSectionsLoading(null);
            });
    }

    React.useEffect(() => {

        handleGetContract();
    }, []);

    const handleSubmit = () => {

        const applicationOid = references.contextsWithContents.contextWithContents.context.applicationOid;
        const contextOid = references.contextsWithContents.contextWithContents.context.oid;
        const contextName = references.contextsWithContents.contextWithContents.context.name;
        const contextDescription = references.contextsWithContents.contextWithContents.context.description;

        const pecData = {
            pecRecipients: {
                items: [
                    {
                        pecAddress: profile.company.pec,
                        recipientName: profile.company.pec,
                    }
                ]
            },
            companyName: profile.company.name,
            productName: profile.campaign.SellingInfo.LcmsContractName,
            proposerName: FE_SETTINGS.addPromoData_proposerName,
        };

        const archiveData = {
            holderFiscalCode: FE_SETTINGS.addPromoData_holderFiscalCode,
            holderVatCode: FE_SETTINGS.addPromoData_holderVatCode,
            holderDescription: FE_SETTINGS.addPromoData_holderDescription,
            holderCountry: FE_SETTINGS.addPromoData_holderCountry,
            counterpartFiscalCode: profile.company.fiscalCode,
            counterpartVatCode: profile.company.vatCode,
            counterpartDescription: profile.company.name,
            productName: profile.campaign.SellingInfo.LcmsContractName,
            companyName: profile.company.name,
        }

        const counterPart = {
            companyName: profile.company.name,
            signatory: {
                name: profile.name,
                surname: profile.surname,
                fiscalCode: profile.fiscalCode,
                email: profile.email,
            },
            companyData: profile.company,
            preservationOfficer: null,    // ?? 
        }

        // si assume che il contratto sia stato creato con due checkbox 1: { key: 0, ... } per esito positivo, { key: 1, ... } per esito negativo
        const radioOptionChoice = sections.section_options.content.radioOptions?.radioOption.find(({ key }) => Boolean(key) === !accepted);

        const addContract: SetPromoDataType.AddContractRequest = {
            username: "",             // __take_from_session__
            usernameOid: Guid.Empty,  // __take_from_session__
            applicationOid,
            applicationName: FE_SETTINGS.addPromoData_applicationName,
            contextOid,
            contextName,
            contextDescription: `${contextDescription}.pdf`,
            base64Content: '',
            enabled: FE_SETTINGS.addPromoData_enabled,
            repeatVerify: FE_SETTINGS.addPromoData_repeatVerify,
            lCMSContractOid: FE_SETTINGS.addPromoData_lCMSContractOid,
            companyOwnCode: profile.company.code,
            signedForCompany: FE_SETTINGS.addPromoData_signedForCompany,
            proposerCode: FE_SETTINGS.addPromoData_proposerCode,
            excludeArchiving: FE_SETTINGS.addPromoData_excludeArchiving,
            pecData,
            archiveData,
            counterPart,
            attachments: { items: [] },
            addContractContents: {
                items: [{
                    accepted,
                    contractOid: FE_SETTINGS.addPromoData_contractOid,
                    localizedContentOid: sections.section_logo.localizedContent.oid,
                    title: String(sections.section_logo.localizedContent.title),
                    text: sections.section_logo.localizedContent.text,
                    subject: sections.section_logo.localizedContent.subject,
                    iso3Code: sections.section_logo.localizedContent.iso3code,
                    position: sections.section_logo.contentRefContext.position,
                    required: sections.section_logo.content.required,
                    optional: sections.section_logo.content.optional,
                    startDate: sections.section_logo.content.publicationDate,
                    endDate: sections.section_logo.contentRefContext.expirationDate,
                    enabled: sections.section_logo.content.enabled,
                    textAreas: sections.section_logo.content.textAreas?.textArea,
                    radioOptionChoice: null,
                },
                {
                    accepted,
                    contractOid: FE_SETTINGS.addPromoData_contractOid,
                    localizedContentOid: sections.section_promo.localizedContent.oid,
                    title: String(sections.section_promo.localizedContent.title),
                    text: getSection(`.${promoSectionKeys.main}`).innerHTML,
                    subject: sections.section_promo.localizedContent.subject,
                    iso3Code: sections.section_promo.localizedContent.iso3code,
                    position: sections.section_promo.contentRefContext.position,
                    required: sections.section_promo.content.required,
                    optional: sections.section_promo.content.optional,
                    startDate: sections.section_promo.content.publicationDate,
                    endDate: sections.section_promo.contentRefContext.expirationDate,
                    enabled: sections.section_promo.content.enabled,
                    textAreas: sections.section_promo.content.textAreas?.textArea,
                    radioOptionChoice: null,
                }, {
                    accepted,
                    contractOid: FE_SETTINGS.addPromoData_contractOid,
                    localizedContentOid: sections.section_conditions.localizedContent.oid,
                    title: String(sections.section_conditions.localizedContent.title),
                    text: getSection(`.${promoSectionKeys.conditions}`).innerHTML,
                    subject: sections.section_conditions.localizedContent.subject,
                    iso3Code: sections.section_conditions.localizedContent.iso3code,
                    position: sections.section_conditions.contentRefContext.position,
                    required: sections.section_conditions.content.required,
                    optional: sections.section_conditions.content.optional,
                    startDate: sections.section_conditions.content.publicationDate,
                    endDate: sections.section_conditions.contentRefContext.expirationDate,
                    enabled: sections.section_conditions.content.enabled,
                    textAreas: sections.section_conditions.content.textAreas?.textArea,
                    radioOptionChoice: null,
                },
                {
                    accepted,
                    contractOid: FE_SETTINGS.addPromoData_contractOid,
                    localizedContentOid: sections.section_options.localizedContent.oid,
                    title: String(sections.section_options.localizedContent.title),
                    text: getSection(`.${promoSectionKeys.options}`).innerHTML,
                    subject: sections.section_options.localizedContent.subject,
                    iso3Code: sections.section_options.localizedContent.iso3code,
                    position: sections.section_options.contentRefContext.position,
                    required: sections.section_options.content.required,
                    optional: sections.section_options.content.optional,
                    startDate: sections.section_options.content.publicationDate,
                    endDate: sections.section_options.contentRefContext.expirationDate,
                    enabled: sections.section_options.content.enabled,
                    textAreas: sections.section_options.content.textAreas?.textArea,
                    radioOptionChoice: {
                        key: String(radioOptionChoice.key),
                        value: radioOptionChoice.value,
                    },
                }
                ]
            },
        }

        const model: SetPromoDataType.Request = {
            token: profile.query_token,
            userEmail: profile.email,
            accepted,
            addContract,
        };

        setLoader(true);
        SetPromoData(model)
            .then(function (res) {
                const code = res.data.status?.code;
                setLoader(false);
                (window as any).aptrinsic("track", "USE_PROMO_V1", {
                    Id: profile.code + '_' + profile.company.vatCode,
                    Accepted: accepted ? "True" : "False",
                });
                if (code === "0") {
                    navigate(`/${accepted ? routes.confirmedContract : routes.notConfirmedContract}`, { replace: true })
                } else {
                    errorNotify(`${errorMessages.SetPromoData[code] || errorMessages?.GeneralError} (Codice errore: ${code || 500})`);
                }
            })
            .catch(function (error) {
                const code = error.status?.code;
                errorNotify(`${errorMessages.GeneralError} (Codice errore: ${code || 500})`);
                setLoader(false);
            });
    }

    return (
        <Box display="flex" gap={2} flexDirection="column" py={2} px={1} maxWidth="1850px">
            <Box sx={{ display: 'flex', flexDirection: 'column' }} p={2}>

                {sections?.section_promo && (<HTMLMainPromoFormatter profile={profile} contract={sections?.section_promo} />)}
                {sections?.section_conditions && (<HTMLPromoConditionsFormatter profile={profile} contract={sections?.section_conditions} />)}
                {sections?.section_options && (<HTMLPromoOptionsFormatter profile={profile} contract={sections?.section_options} setConfirm={setAccepted} />)}

                {(sectionsLoading === null) ? (
                    <Box
                        sx={{
                            display: 'flex',
                            height: 'calc(100vh - 180px)',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >
                        <IconButton onClick={handleGetContract}>
                            <AiOutlineReload fontSize={50} opacity={0.3} />
                        </IconButton>
                        <StyledPageComment>Si è verificato un problema con il caricamento della promozione.</StyledPageComment>
                    </Box>
                ) : sectionsLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            height: 'calc(100vh - 180px)',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >
                        <StyledPageComment>Caricamento del contratto in corso...</StyledPageComment>
                    </Box>
                ) : null}
            </Box>
            <Stack spacing={2} direction="row" justifyContent="flex-end" pb={2}>
                <VdsButton color="primary" variant="contained" onClick={handleSubmit} disabled={accepted === null}>Conferma scelta{accepted && ". Acquista ora cliccando qui"}</VdsButton>
            </Stack>
        </Box>
    );
}
